export class ConfigHelper {
  constructor(config, searchType) {
    this.config = config;
    this.searchType = searchType;
  }

  getConfig() {
    return this.config;
  }

  capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  getTitleField() {
    return this.config.titleField || 'title';
  }

  getBaseURLField() {
    if (window.location.hostname === 'localhost') {
      return 'https://dev.historicengland.org.uk';
    }
    return '';
  }

  getUrlField() {
    return this.config.urlField;
  }

  getThumbnailField() {
    return this.config.thumbnailField;
  }

  getHostIdentifier() {
    return this.config.hostIdentifier;
  }

  getEndpoints(searchType) {
    return {
      searchEndpoint: this.getEnvironmentDataValue(
        'REACT_APP_' + searchType + '_SEARCH_ENDPOINT',
      ),
      autocompleteEndpoint: this.getEnvironmentDataValue(
        'REACT_APP_' + searchType + '_AUTOCOMPLETE_ENDPOINT',
      ),
      downloadEndpoint: this.getEnvironmentDataValue(
        'REACT_APP_' + searchType + '_DOWNLOAD_ENDPOINT',
      ),
      locationSuggestionsEndpoint: this.getEnvironmentDataValue(
        'REACT_APP_LOCATION_SUGGESTIONS_ENDPOINT',
      ),
    };
  }

  getPageSizeOptions() {
    return this.config.pageSizeOptions
      ? this.config.pageSizeOptions
      : [24, 48, 96];
  }

  buildSearchOptionsFromConfig() {
    const config = this.config;
    const searchFields = (
      this.config.searchFields ||
      this.config.fields ||
      []
    ).reduce((acc, n) => {
      acc = acc || {};
      acc[n] = {};
      return acc;
    }, undefined);

    const resultFields = (config.resultFields || config.fields || []).reduce(
      (acc, n) => {
        acc = acc || {};
        acc[n] = {
          raw: {},
          snippet: {
            size: 100,
            fallback: true,
          },
        };
        return acc;
      },
      undefined,
    );

    // We can't use url, thumbnail, or title fields unless they're actually
    // in the reuslts.
    if (config.urlField) {
      resultFields[config.urlField] = {
        raw: {},
        snippet: {
          size: 100,
          fallback: true,
        },
      };
    }

    if (config.thumbnailField) {
      resultFields[config.thumbnailField] = {
        raw: {},
        snippet: {
          size: 100,
          fallback: true,
        },
      };
    }

    if (config.titleField) {
      resultFields[config.titleField] = {
        raw: {},
        snippet: {
          size: 100,
          fallback: true,
        },
      };
    }

    const searchOptions = {};
    searchOptions.result_fields = resultFields;
    searchOptions.search_fields = searchFields;
    return searchOptions;
  }

  buildFacetConfigFromConfig() {
    const facets = (this.config.facets || []).reduce((acc, n) => {
      acc = acc || {};
      acc[n.field] = {
        type: 'value',
        size: 250,
      };
      return acc;
    }, undefined);

    return facets;
  }

  buildDisjunctiveFacetsFromConfig() {
    const disjunctiveFacets = this.config?.facets
      ? this.config.facets
          .filter(facet => facet.disjunctive)
          .map(facet => facet.field)
      : [];
    return disjunctiveFacets;
  }

  buildSortOptionsFromConfig() {
    return [
      {
        name: 'Relevance',
        value: '',
        direction: '',
      },
      ...(this.config.sortFields || []).reduce((acc, sortField) => {
        let sortFieldLabel = this.capitalizeFirstLetter(sortField);

        const titleField = this.getTitleField();
        if (sortField === titleField || sortField === 'name_site') {
          sortFieldLabel = 'Title';
        }

        if (sortField === 'image') {
          acc.push({
            name: `Images first`,
            value: sortField,
            direction: 'asc',
          });
          acc.push({
            name: `Images last`,
            value: sortField,
            direction: 'desc',
          });
          return acc;
        }

        if (sortField === 'date') {
          acc.push({
            name: `${sortFieldLabel} (Oldest first)`,
            value: sortField,
            direction: 'asc',
          });
          acc.push({
            name: `${sortFieldLabel} (Newest first)`,
            value: sortField,
            direction: 'desc',
          });
          return acc;
        }

        if (this.searchType === 'nhle') {
          if (sortField === 'reference') {
            acc.push({
              name: `List Entry No. (High-Low)`,
              value: sortField,
              direction: 'desc',
            });
            acc.push({
              name: `List Entry No. (Low-High)`,
              value: sortField,
              direction: 'asc',
            });
            return acc;
          }
        }

        if (this.searchType === 'heritage-gateway') {
          if (sortField === 'reference') {
            acc.push({
              name: `Ref No. (Descending)`,
              value: sortField,
              direction: 'desc',
            });
            acc.push({
              name: `Ref No. (Ascending)`,
              value: sortField,
              direction: 'asc',
            });
            return acc;
          }

          if (sortField === 'dateAdded') {
            acc.push({
              name: `Date updated (Most recent first)`,
              value: sortField,
              direction: 'desc',
            });
            return acc;
          }
        }

        acc.push({
          name: `${sortFieldLabel} (A-Z)`,
          value: sortField,
          direction: 'asc',
        });
        acc.push({
          name: `${sortFieldLabel} (Z-A)`,
          value: sortField,
          direction: 'desc',
        });
        return acc;
      }, []),
    ];
  }

  getEnvironmentDataValue(key) {
    const envData = process.env;
    return envData ? envData[key] : undefined;
  }
}
